import type { DataApiProject, DataApiProjectDto } from '@core/api/data-api.facade';
import { isProjectListingDraft } from '@features/dashboard/components/projects/utils/project-listing-draft';
import { resolveFundraiseStatus } from '@features/launchpad/services/fundraising-status';
import type { LaunchpadOffering } from '@features/launchpad/models/launchpad.types';
import {
  readProjectBlockchainIds,
  type ProjectIdsSource,
} from './project-network-ids';

export type LaunchpadProjectSource = DataApiProject | DataApiProjectDto;

function parseTotalRaised(value: string | null | undefined): number {
  if (value == null || value.trim() === '') {
    return 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function readBlockchainKeys(
  project: ProjectIdsSource,
  blockchainKeyById?: ReadonlyMap<number, string>,
): string[] {
  const entity = project as DataApiProject;
  const fromJoin =
    entity.projectBlockchains
      ?.map((pb) => pb.blockchain?.key)
      .filter((key): key is string => !!key) ?? [];
  if (fromJoin.length > 0) {
    return fromJoin;
  }

  if (!blockchainKeyById) {
    return [];
  }

  return readProjectBlockchainIds(project)
    .map((id) => blockchainKeyById.get(id))
    .filter((key): key is string => !!key);
}

export function toLaunchpadOffering(
  project: LaunchpadProjectSource,
  options?: {
    blockchainKeyById?: ReadonlyMap<number, string>;
    totalRaisedAdjustment?: number;
  },
): LaunchpadOffering | null {
  const projectId = project.projectId;
  if (projectId == null) {
    return null;
  }

  const adjustment = options?.totalRaisedAdjustment ?? 0;

  return {
    projectId,
    projectName: project.projectName,
    tokenSymbol: project.tokenSymbol,
    projectDescription: project.projectDescription ?? undefined,
    fundraisingGoal: project.fundraisingGoal,
    totalRaised: parseTotalRaised(project.totalRaised) + adjustment,
    fundraiseStatus: resolveFundraiseStatus(
      project.fundraisingStartTime,
      project.fundraisingEndTime,
    ),
    fundraisingStartTime: project.fundraisingStartTime,
    fundraisingEndTime: project.fundraisingEndTime,
    tokenDistributionDate: project.tokenDistributionDate,
    launchDate: project.launchDate,
    blockchainKeys: readBlockchainKeys(
      project,
      options?.blockchainKeyById,
    ),
    pitchDeckUrl: project.pitchDeck ?? undefined,
    whitepaperUrl: project.whitepaper ?? undefined,
    websiteUrl: project.website ?? undefined,
    exclusiveIdo: project.exclusiveIdo,
    listingDraft: isProjectListingDraft(project as DataApiProject),
  };
}

export function toLaunchpadOfferings(
  projects: readonly LaunchpadProjectSource[],
  options?: {
    blockchainKeyById?: ReadonlyMap<number, string>;
    totalRaisedAdjustments?: ReadonlyMap<number, number>;
  },
): LaunchpadOffering[] {
  return projects
    .map((project) => {
      const adjustment =
        project.projectId != null
          ? (options?.totalRaisedAdjustments?.get(project.projectId) ?? 0)
          : 0;
      return toLaunchpadOffering(project, {
        blockchainKeyById: options?.blockchainKeyById,
        totalRaisedAdjustment: adjustment,
      });
    })
    .filter((offering): offering is LaunchpadOffering => offering != null);
}
